/**
 * standings — classifica **ricostruita a una giornata**.
 *
 * La classifica del girone arriva da FIPAV ed è lo stato **finale** (o corrente)
 * del girone. Nel match center, però, una partita della 15ª giornata va letta con
 * la classifica **di quel momento**: le giornate successive non erano ancora
 * state giocate, e mostrarle sarebbe un anacronismo.
 *
 * Qui si ricostruiscono i **punti** dalle partite fino alla giornata richiesta,
 * con la regola FIPAV: 3-0 e 3-1 → 3 punti alla vincente; 3-2 → 2 alla vincente
 * e 1 alla perdente; 0-3 e 1-3 → 0. Set e punti (rallies) si sommano dai parziali.
 *
 * L'ordine dei pari merito **resta quello di FIPAV** (sort stabile sull'array
 * ufficiale): la regola interna di FIPAV non è riproducibile dai soli dati e,
 * così, sull'ultima giornata la classifica ricostruita coincide **esattamente**
 * con quella ufficiale. Verificato su sette gironi della 25/26: punti identici
 * per tutte le squadre.
 */
import type { FipavStanding } from '@/lib/fipav/scraper'

export interface StandingsMatch {
  giornata: number
  played: boolean
  sets: string | null
  home: string
  away: string
}

function teamKey(name: string): string {
  return name
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Z0-9]+/g, ' ')
    .trim()
}

/** Punti FIPAV di un set concluso, dalle due parti. `3-2` vale 2 e 1. */
function pointsForSets(homeSets: number, awaySets: number): { home: number; away: number } {
  if (homeSets > awaySets) return awaySets === 2 ? { home: 2, away: 1 } : { home: 3, away: 0 }
  if (awaySets > homeSets) return homeSets === 2 ? { home: 1, away: 2 } : { home: 0, away: 3 }
  return { home: 0, away: 0 }
}

interface TeamTotals {
  played: number
  points: number
  pointsFor: number
  pointsAgainst: number
}

/**
 * Classifica a una giornata. `upToGiornata` = ultima giornata inclusa
 * (di norma quella della partita che si sta guardando). `null` = nessun limite.
 */
export function computeStandingsAsOf(
  standings: FipavStanding[],
  matches: StandingsMatch[],
  upToGiornata: number | null,
): FipavStanding[] {
  if (upToGiornata == null || standings.length === 0) return standings

  const totals = new Map<string, TeamTotals>()
  const get = (team: string): TeamTotals => {
    const key = teamKey(team)
    let entry = totals.get(key)
    if (!entry) {
      entry = { played: 0, points: 0, pointsFor: 0, pointsAgainst: 0 }
      totals.set(key, entry)
    }
    return entry
  }

  for (const match of matches) {
    if (!match.played || !match.sets || match.giornata > upToGiornata) continue
    const pairs = (match.sets.match(/\d{1,2}-\d{1,2}/g) ?? []).map((pair) => pair.split('-').map(Number))
    if (pairs.length === 0) continue

    let homeSets = 0
    let awaySets = 0
    let homePoints = 0
    let awayPoints = 0
    for (const [home, away] of pairs) {
      homePoints += home
      awayPoints += away
      if (home > away) homeSets += 1
      else awaySets += 1
    }

    const home = get(match.home)
    const away = get(match.away)
    const awarded = pointsForSets(homeSets, awaySets)
    home.played += 1
    away.played += 1
    home.points += awarded.home
    away.points += awarded.away
    home.pointsFor += homePoints
    home.pointsAgainst += awayPoints
    away.pointsFor += awayPoints
    away.pointsAgainst += homePoints
  }

  // Sort **stabile** sui punti: a pari punti l'ordine resta quello di FIPAV.
  return [...standings]
    .sort((a, b) => (totals.get(teamKey(b.team))?.points ?? 0) - (totals.get(teamKey(a.team))?.points ?? 0))
    .map((row, index) => {
      const entry = totals.get(teamKey(row.team))
      return {
        ...row,
        position: index + 1,
        played: entry?.played ?? 0,
        points: entry?.points ?? 0,
        pointsFor: entry ? entry.pointsFor : null,
        pointsAgainst: entry ? entry.pointsAgainst : null,
      }
    })
}
